import React from "react";

const findScrollParents = (element) => {
  if (!element) return [];
  let result = findScrollParents(element.parentElement);
  if (element.scrollHeight > element.offsetHeight)
    result = [{ vertical: true, element }, ...result];
  if (element.scrollWidth > element.offsetWidth) result = [{ vertical: false, element }, ...result];
  return result;
};

export const DragDropReactContext = React.createContext({});

export const DragDropConsumer = DragDropReactContext.Consumer;
export class DragDropContext extends React.Component {
  state = { dragging: null };
  droppables = {};
  dropResult = {};
  dragOver = null;
  mouse = [0, 0];
  dragOverElement = null;
  animFrame = 0;
  scrollUpdate = () => {
    const mouse = this.mouse;
    const linearScroll = (a, b, w) => {
      const t = Math.min(99999, (b - a) / 4);
      if (w >= b - t && w <= b) return ((w - (b - t)) / t) * 20;
      if (w <= a + t && w >= a) return ((w - (a + t)) / t) * 20;
      return 0;
    };
    if (this.dragOverElement) {
      const scrolls = findScrollParents(this.dragOverElement);
      scrolls.map(({ vertical, element }) => {
        const rect = element.getBoundingClientRect();
        if (vertical) element.scrollBy(0, linearScroll(rect.top, rect.bottom, mouse[1]));
        if (!vertical) element.scrollBy(linearScroll(rect.left, rect.right, mouse[0]), 0);
      });
    }
    this.animFrame = requestAnimationFrame(this.scrollUpdate);
  };
  findDroppable(type, ele) {
    if (!ele) return null;
    const res = Object.values(this.droppables).find((d) => d.innerRef.current === ele);
    if (res && res.props.type === type) return res;
    return this.findDroppable(type, ele.parentElement);
  }
  touchUpdate = (e) => {
    this.mouseUpdate({
      clientX: e.touches[0].clientX,
      clientY: e.touches[0].clientY,
      type: e.type,
    });
  };
  mouseUpdate = (e, dragging, callback) => {
    let { clientX, clientY, buttons = 1, target = null } = e;
    if (!target) target = document.elementFromPoint(clientX, clientY);
    if (buttons === 0) this.endDrag();
    this.mouse = [clientX, clientY];
    const _dragging = dragging || this.state.dragging;
    if (_dragging) {
      _dragging.mouse = this.mouse;
      const drop = this.findDroppable(_dragging.props.type, target);
      this.setDragOver(drop, callback);
      this.dragOverElement = target || null;
    }
  };
  beginDrag = (e, target) => {
    e = {
      clientX: e.clientX,
      clientY: e.clientY,
      target: e.target,
      buttons: e.buttons,
      type: e.type,
    };
    this.setState({ dragging: target }, () => {
      this.mouseUpdate(e, target, () => target.draggingUpdate());
      if (e.type.includes("mouse")) document.addEventListener("mousemove", this.mouseUpdate);
      this.animFrame = requestAnimationFrame(this.scrollUpdate);
      if (e.type === "touchstart") document.addEventListener("touchmove", this.touchUpdate);
    });
  };
  setDragOver = (dragOver, callback) => {
    if (this.dragOver && this.dragOver !== dragOver) this.dragOver.endListening();
    this.dragOver = dragOver;
    if (this.dragOver) this.dragOver.beginListening(callback);
    else this.dropResult = null;
  };
  endDrag = () => {
    cancelAnimationFrame(this.animFrame);
    document.removeEventListener("touchmove", this.touchUpdate);
    document.removeEventListener("mousemove", this.mouseUpdate);
    const { onDragEnd } = this.props;
    let dragEndResult = null;
    if (this.state.dragging && this.dropResult) {
      const dragging = this.state.dragging;
      const sourceId = dragging.context.source.props.id;
      const destId = this.dropResult.droppableId;
      dragEndResult = {
        draggableId: dragging.props.id,
        type: this.dropResult.type,
        source: {
          droppableId: sourceId,
          index: dragging.props.index,
        },
        destination: {
          droppableId: destId,
          index:
            this.dropResult.index +
            (destId === sourceId && this.dropResult.index > dragging.props.index ? -1 : 0),
        },
      };
    }
    this.setState({ dragging: null }, () => {
      this.setDragOver(null);
      if (onDragEnd && dragEndResult) onDragEnd(dragEndResult);
      this.dropResult = {};
    });
  };
  registerDroppable = (droppable) => {
    this.droppables[`${droppable.props.type}-${droppable.props.id}`] = droppable;
  };
  render = () => {
    return (
      <DragDropReactContext.Provider
        value={{
          dragging: this.state.dragging,
          beginDrag: this.beginDrag,
          endDrag: this.endDrag,
          registerDroppable: this.registerDroppable,
          updateDropResult: (d, i) => {
            this.dropResult = { type: d.props.type, droppableId: d.props.id, index: i };
          },
        }}
      >
        {this.props.children}
      </DragDropReactContext.Provider>
    );
  };
}
